import React, { useEffect, useState } from 'react'
import dynamic from 'next/dynamic'
import axios, { AxiosError } from 'axios'
import { PRODUCT_TYPES_WITHOUT_COLORS } from '@consts/index'
import { getProductCategory } from '@helpers/products'
import { isProdEnv } from '@helpers/env'
import logBuildError from '@helpers/misc/logBuildError'
import ProductList, { ProductListProps } from '@components/product/ProductList'
import { useRouter } from 'next/router'
import { ProductByType, ProductHandles } from 'types/product'
import { default as NextError } from 'next/error'
import { CustomError } from '@helpers/errors'
import {
  getProductHandles,
  getProductsByType
} from '../../server/api/graphqlContentService/products'
import {
  fetchProductPageCMSData,
  ProductPageCMSData
} from '@api/fetchProductPageCMSData'
import {
  createProductsData,
  fetchProductDiscount,
  getDiscountProductTypes,
  getProductsListWithTabs
} from '@lib/productPageUtils'
import { sizingWidgetDataManager } from '@helpers/products/sizingWidgetData'
import { trustpilotDataManager } from '@helpers/products/trustpilotData'

const GiftCard = dynamic(() => import('@components/product/GiftCard'))
const NotFound = dynamic(() => import('../404'))

const handleProductsBuildError = logBuildError('products')

const shouldFailOnCMSDataError = () =>
  process.env.NODE_ENV === 'production' && isProdEnv()

const emptyServerCMSData: ProductPageCMSData = {
  productsBreakdown: null,
  careCopy: null,
  productBenefits: null,
  productStatementSection: null,
  discountByProductType: {}
}

type ProductPageProps = {
  productCategory: string
  productType: string
  statusCode: number
} & ProductListProps

function ProductsPage(props: ProductPageProps) {
  const [contentfulPreviewData, setContentfulPreviewData] = useState({})
  const { query } = useRouter()
  const productHandle = Array.isArray(query.productHandle)
    ? query.productHandle[0]
    : query.productHandle
  const productProps = { ...props, ...contentfulPreviewData }

  useEffect(() => {
    const fetchPreviewCMSData = async () => {
      try {
        const discountProductTypes = getDiscountProductTypes(props.productsList)
        const previewCMSData = await fetchProductPageCMSData({
          productType: props.productType,
          productCategory: props.productCategory || '',
          productTypes: props.productTypes,
          discountProductTypes,
          productHandle: productHandle || '',
          preview: true
        })
        setContentfulPreviewData(previewCMSData)
      } catch (e) {
        console.error(e)
      }
    }
    if (query.preview === 'true' && productHandle) {
      fetchPreviewCMSData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query.preview, productHandle])

  if (!props.productsList?.length || props.statusCode === 404) {
    return <NotFound />
  }

  if (props.statusCode !== 200) {
    return <NextError statusCode={props.statusCode} />
  }

  if (props.productType === 'Gift Card') {
    return <GiftCard {...productProps} />
  }

  return <ProductList {...productProps} />
}

function productHandlesManager() {
  let handles: ProductHandles

  return {
    get: async function () {
      if (!handles) {
        handles = ((await getProductHandles()) || {}) as ProductHandles
      }

      return handles
    }
  }
}

const handlesManager = productHandlesManager()
const sizingWidgetManager = sizingWidgetDataManager()
const trustpilotManager = trustpilotDataManager()
let buildContainsErrors = false

export async function getStaticPaths() {
  const { activeHandles, inactiveHandles } = await handlesManager.get()

  if (inactiveHandles.length > 0) {
    handleProductsBuildError({
      errorLevel: 'warning',
      properties: {
        inactiveHandles,
        message: 'Inactive Handles'
      }
    })
  }

  const paths = activeHandles.map(product => ({
    params: { productHandle: product.handle }
  }))

  return { paths, fallback: false }
}

export async function getStaticProps({
  params
}: {
  params: { productHandle: string }
}) {
  const { productHandle } = params
  let productType: string | undefined = undefined

  try {
    const { activeHandles } = await handlesManager.get()
    const lastProductHandle = activeHandles?.at(-1)?.handle

    const productData = activeHandles?.find(
      product => product.handle === productHandle
    )

    productType = productData?.product_type || ''
    const groupByTag = productData?.group_tag || ''
    const pageData = await getProductsByType(productType, groupByTag)

    if (!pageData || typeof pageData !== 'object') {
      buildContainsErrors = true
      throw new CustomError('Invalid response from getProductsByType')
    }

    const { productTypes: productTypesData, tabsOrder, errors } = pageData || {}

    if (errors && errors.length > 0) {
      buildContainsErrors = true
      throw new CustomError(`API error - ${errors}`)
    }

    if (!productTypesData) {
      buildContainsErrors = true
      throw new CustomError(
        `CAS data is missing - ${productType} - ${productHandle}`
      )
    }

    if (tabsOrder?.length && tabsOrder?.length !== productTypesData?.length) {
      buildContainsErrors = true
      throw new CustomError(
        `CAS: tabs order exists but length is different than product types length`
      )
    }

    const productTypes = productTypesData.map(
      (item: ProductByType) => item.product_type
    )
    const discountProductTypes = getDiscountProductTypes(productTypesData)

    const discountByProductType = await fetchProductDiscount(
      discountProductTypes
    )

    const productsData = createProductsData(
      productTypesData,
      discountByProductType
    )

    if (!productsData?.length) {
      buildContainsErrors = true
      throw new Error(`All product types data is missing`)
    }

    const { allProducts, productsList } = getProductsListWithTabs(
      productsData,
      tabsOrder
    )

    const currentProductData = allProducts.find(
      item => item?.handle === productHandle
    )

    if (!currentProductData) {
      buildContainsErrors = true
      throw new CustomError(
        `Can't find data for this page. Product type has other products data but missing for this product handle`
      )
    }

    if (
      !currentProductData.color?.primary &&
      !PRODUCT_TYPES_WITHOUT_COLORS.includes(productType)
    ) {
      buildContainsErrors = true
      throw new CustomError(`Missing color data - ${productType}`)
    }
    const currentProductTypeData = productsData.find(
      data => data?.product_type === productType
    )

    if (
      currentProductTypeData?.color_categories?.length &&
      !currentProductData.color?.category
    ) {
      buildContainsErrors = true
      throw new CustomError(
        `Product color not included in a category - ${productType}`
      )
    }

    const productCategory = getProductCategory(
      currentProductData.options,
      productType
    )

    let serverCMSData = emptyServerCMSData

    try {
      serverCMSData = await fetchProductPageCMSData({
        productType,
        productCategory: productCategory || '',
        productTypes: productTypes,
        discountProductTypes,
        productHandle: productHandle,
        preview: false
      })
    } catch (error) {
      if (shouldFailOnCMSDataError()) {
        throw error
      }

      handleProductsBuildError({
        errorLevel: 'warning',
        properties: {
          handle: productHandle,
          productType,
          message: 'Failed to fetch product CMS data',
          error
        }
      })
    }

    const sizingWidgetData = await sizingWidgetManager.get({
      handle: productHandle,
      productCategory: productCategory || ''
    })

    const { trustpilotRatings, trustpilotReviews, trustpilotDrawerReviews } =
      await trustpilotManager.get(productType)

    const productIds = allProducts.map(product => product?.id)

    const productProps = {
      productCategory,
      productsList,
      productIds,
      productType,
      productTypes,
      meta: currentProductData.meta,
      sizingWidgetData,
      trustpilotRatings,
      trustpilotReviews,
      trustpilotDrawerReviews,
      ...serverCMSData
    }

    if (productHandle === lastProductHandle && buildContainsErrors) {
      throw new CustomError('Build contains errors')
    }

    return {
      props: {
        ...productProps,
        statusCode: 200
      }
    }
  } catch (e) {
    const error = e as typeof CustomError | AxiosError
    const handle = productHandle

    if (
      (error instanceof CustomError && error.status === 404) ||
      (axios.isAxiosError(error) && error.response?.status === 404)
    ) {
      handleProductsBuildError({
        errorLevel: 'warning',
        properties: {
          handle,
          message: error.message || '404 - Product does not exist'
        }
      })
    } else {
      const errorMessage =
        (error instanceof CustomError && error.message) ||
        (axios.isAxiosError(error) && error.message)

      handleProductsBuildError({
        errorLevel: 'critical',
        properties: {
          handle,
          productType: productType,
          ...(axios.isAxiosError(error) && error.response
            ? {
                path: error.request.path,
                error: error?.message
              }
            : { error: errorMessage })
        }
      })
      if (
        process.env.NODE_ENV === 'production' &&
        isProdEnv() &&
        errorMessage === 'Build contains errors'
      ) {
        throw new Error(`Product page failed to export`)
      }
    }

    return {
      props: {
        statusCode:
          axios.isAxiosError(error) && error.response
            ? error.response.status
            : 404
      }
    }
  }
}

export default ProductsPage
